/**
 * Scope model — bridges the backend's flat `allowed_scopes` string and the
 * grouped UI picker.
 *
 * A Squad agent's capability ceiling is a single space-delimited string of Core
 * scope-catalog codes (e.g. `"org:members:read account:profile:write"`). The
 * picker works with a `Set<string>` of those codes; these helpers parse/serialise
 * it and group the catalog (from Core's `/authorization/v2/oidc/scopes/`) by
 * app → resource for rendering.
 */

/** One catalog row as returned by Core (snake_case mapped to camelCase here). */
export type ScopeRow = {
  code: string
  description: string
  resource: string
  action: string
  app: string
  enforcedByCore: boolean
  deprecated: boolean
}

export type ScopeResourceGroup = { resource: string; rows: ScopeRow[] }
export type ScopeAppGroup = { app: string; resources: ScopeResourceGroup[] }

/** `"a b  c"` → `Set{a,b,c}` (whitespace-tolerant, empties dropped). */
export const parseAllowedScopes = (
  raw: string | null | undefined
): Set<string> => new Set((raw ?? '').split(/\s+/).filter(Boolean))

/** `Set{b,a}` → `"a b"` — sorted + single-space, matching Squad's normalisation. */
export const serializeAllowedScopes = (codes: Set<string>): string =>
  Array.from(codes).sort().join(' ')

/**
 * Group catalog rows by app, then by resource, preserving the order in which
 * apps/resources first appear (the catalog is already declared in a sensible
 * order: account, org, scim, mcp, then crm).
 */
export const groupScopes = (rows: ScopeRow[]): ScopeAppGroup[] => {
  const byApp = new Map<string, Map<string, ScopeRow[]>>()
  for (const row of rows) {
    if (!byApp.has(row.app)) byApp.set(row.app, new Map())
    const byResource = byApp.get(row.app)!
    if (!byResource.has(row.resource)) byResource.set(row.resource, [])
    byResource.get(row.resource)!.push(row)
  }
  return Array.from(byApp, ([app, byResource]) => ({
    app,
    resources: Array.from(byResource, ([resource, rows]) => ({
      resource,
      rows
    }))
  }))
}
